import type { StoreTicket, StoreTicketBucket } from "@khazzin/api-client";
import { formatDateTime } from "@khazzin/i18n/dates";
import { StatusChip, slaOverdue, TicketStatusChip } from "@khazzin/ui/patterns";
import { EmptyState, PageHeader } from "@khazzin/ui/patterns/page";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import { Button, SegmentedControl } from "@khazzin/ui/primitives";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { useStoreScope } from "../../app/shell/scope";
import { OpenTicketDrawer } from "./OpenTicketDrawer";
import { linkedPath, ticketsKey } from "./tickets";

/**
 * Tickets (EP10-S12, DA-10-11): the store's tickets and claims in three tabs —
 * Open, Waiting on you, Resolved — with the SLA due in red once it has
 * passed. New ticket opens a general question; a claim is opened from the
 * order it is about.
 */

const TABS: readonly StoreTicketBucket[] = ["open", "waiting", "resolved"];

export function TicketsPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const { store, context } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const [params, setParams] = useSearchParams();
	const raw = params.get("tab");
	const tab: StoreTicketBucket = TABS.includes(raw as StoreTicketBucket)
		? (raw as StoreTicketBucket)
		: "open";
	const [asking, setAsking] = useState(false);
	const at = (iso: string) => formatDateTime(iso, { locale, timeZone });

	const page = useCursorPagination<StoreTicket>(
		(cursor, limit, signal) =>
			api
				.listPortalTickets({ bucket: tab, ...(cursor === null ? {} : { cursor }), limit, signal })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...ticketsKey(storeId), "list", tab], enabled: storeId !== "" },
	);

	const columns: Column<StoreTicket>[] = [
		{
			id: "number",
			headerKey: "portal.tickets.columns.number",
			mono: true,
			card: "wide",
			cell: (row) => <Link to={`/tickets/${encodeURIComponent(row.id)}`}>{row.number}</Link>,
		},
		{
			id: "type",
			headerKey: "portal.tickets.columns.type",
			cell: (row) => (
				<StatusChip tone="neutral" size="sm">
					{t(`tickets.type.${row.type}`)}
				</StatusChip>
			),
		},
		{ id: "subject", headerKey: "portal.tickets.columns.subject", cell: (row) => row.subject },
		{
			id: "linked",
			headerKey: "portal.tickets.columns.linked",
			cell: (row) => {
				const link = row.linkedEntity;
				if (link === null || link === undefined) {
					return "—";
				}
				const text = `${t(`tickets.link.${link.kind}`)} ${link.label ?? link.id}`;
				const to = linkedPath(link.kind, link.id);
				return to === null ? text : <Link to={to}>{text}</Link>;
			},
		},
		{
			id: "status",
			headerKey: "portal.tickets.columns.status",
			cell: (row) => <TicketStatusChip status={row.status} t={t} />,
		},
		{
			id: "sla",
			headerKey: "portal.tickets.columns.sla",
			cell: (row) =>
				slaOverdue(row) ? (
					<span className="font-semibold text-danger-fg">
						{t("tickets.sla.overdue")} · {at(row.slaDueAt)}
					</span>
				) : (
					at(row.slaDueAt)
				),
		},
		{
			id: "updated",
			headerKey: "portal.tickets.columns.updated",
			cell: (row) => at(row.updatedAt),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.tickets.title")}
				subtitle={t("portal.tickets.subtitle")}
				primaryAction={
					<Button variant="primary" className="min-h-12 md:min-h-8" onClick={() => setAsking(true)}>
						{t("portal.tickets.new")}
					</Button>
				}
			/>
			<div className="overflow-x-auto">
				<SegmentedControl<StoreTicketBucket>
					aria-label={t("portal.tickets.title")}
					value={tab}
					onChange={(next) => {
						setParams({ tab: next }, { replace: true });
						page.reset();
					}}
					options={TABS.map((value) => ({ value, label: t(`portal.tickets.tabs.${value}`) }))}
				/>
			</div>
			<DataTable<StoreTicket>
				screenKey="portal.tickets"
				caption={t("portal.tickets.title")}
				t={t}
				columns={columns}
				rowKey={(row) => row.id}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				empty={
					<EmptyState
						title={t("portal.tickets.empty.title")}
						body={t("portal.tickets.empty.body")}
					/>
				}
			/>
			{asking && <OpenTicketDrawer open onOpenChange={setAsking} source="none" />}
		</div>
	);
}
